import { axiosInstance } from './axiosInstance';
import { progressFromSequentialItems } from '../components/common/FileTransferProgressDialog';
import { getStoredRefreshToken, refreshAccessToken } from './authTokenRefresh';

export interface ControlArchiveAttachment {
  id: number;
  original_name: string;
  content_type?: string | null;
  size_bytes: number;
  created_at: string;
}

export interface ControlArchivePost {
  id: number;
  title: string;
  body: string;
  author_user_id?: number | null;
  author_username?: string | null;
  author_full_name?: string | null;
  author_label: string;
  attachment_count: number;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  can_delete?: boolean;
}

export interface ControlArchivePostDetail extends ControlArchivePost {
  attachments: ControlArchiveAttachment[];
}

export type UploadFileProgressInfo = {
  overallPercent: number | null;
  filePercent: number | null;
  fileIndex: number;
  fileCount: number;
  currentFilename: string;
};

const BASE = '/api/control-archive';

function stripFormContentType(data: unknown, headers: Record<string, unknown>) {
  if (typeof FormData !== 'undefined' && data instanceof FormData && headers) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  return data;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function refreshTokenQuiet(): Promise<void> {
  if (!getStoredRefreshToken()) return;
  try {
    await refreshAccessToken();
  } catch {
    /* ignore */
  }
}

export const controlArchiveService = {
  async list(): Promise<{
    items: ControlArchivePost[];
    total: number;
    allow_delete?: boolean;
  }> {
    const res = await axiosInstance.get<{
      items: ControlArchivePost[];
      total: number;
      allow_delete?: boolean;
    }>(BASE);
    return res.data;
  },

  async getDetail(id: number): Promise<ControlArchivePostDetail> {
    const res = await axiosInstance.get<ControlArchivePostDetail>(`${BASE}/${id}`);
    return res.data;
  },

  /**
   * 글 메타 등록 → 첨부별 raw 스트림 업로드.
   * 5GB급에서 multipart 임시저장+재복사를 피하고, 클라이언트가 보낸 바이트가 곧 최종 파일이다.
   */
  async create(payload: {
    title: string;
    body: string;
    files?: File[];
    onUploadProgress?: (percent: number | null) => void;
    onUploadFileProgress?: (info: UploadFileProgressInfo) => void;
  }): Promise<ControlArchivePostDetail> {
    const files = (payload.files || []).slice(0, 5);
    await refreshTokenQuiet();

    const meta = new FormData();
    meta.append('title', payload.title);
    meta.append('body', payload.body);

    const created = await axiosInstance.post<ControlArchivePostDetail>(BASE, meta, {
      timeout: 60000,
      transformRequest: [stripFormContentType],
    });
    let detail = created.data;
    if (files.length === 0) {
      return detail;
    }

    const postId = detail.id;
    const items = files.map((f) => ({ name: f.name, sizeBytes: f.size }));

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        await refreshTokenQuiet();

        let lastKnown: number | null = 0;
        const emit = (filePct: number | null) => {
          if (filePct != null) lastKnown = filePct;
          const info = progressFromSequentialItems(
            items,
            i,
            filePct != null ? filePct : lastKnown
          );
          if (filePct == null) info.filePercent = null;
          payload.onUploadFileProgress?.(info);
          payload.onUploadProgress?.(info.overallPercent);
        };
        emit(0);

        try {
          await axiosInstance.put(
            `${BASE}/${postId}/attachments/stream`,
            file,
            {
              timeout: 0,
              maxBodyLength: Infinity,
              maxContentLength: Infinity,
              params: {
                filename: file.name,
                content_type: file.type || undefined,
              },
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
              },
              transformRequest: [(data) => data],
              onUploadProgress: (ev) => {
                if (ev.total && ev.total > 0) {
                  emit((ev.loaded * 100) / ev.total);
                } else if (file.size > 0 && ev.loaded > 0) {
                  emit(Math.min(99, (ev.loaded * 100) / file.size));
                } else {
                  emit(null);
                }
              },
            }
          );
        } catch (fileErr: any) {
          // 전송은 끝났는데 응답(502/커넥션)만 실패한 경우: 서버 반영 여부 확인
          const latest = await axiosInstance
            .get<ControlArchivePostDetail>(`${BASE}/${postId}`)
            .then((r) => r.data)
            .catch(() => null);
          const count = latest?.attachment_count ?? 0;
          if (count > i) {
            emit(100);
            continue;
          }
          throw fileErr;
        }
        emit(100);
      }
      return await controlArchiveService.getDetail(postId);
    } catch (err) {
      try {
        const latest = await axiosInstance.get<ControlArchivePostDetail>(`${BASE}/${postId}`);
        if ((latest.data.attachment_count ?? 0) >= files.length) {
          return latest.data;
        }
        if ((latest.data.attachment_count ?? 0) === 0) {
          await axiosInstance.delete(`${BASE}/${postId}`);
        }
      } catch {
        /* ignore cleanup errors */
      }
      throw err;
    }
  },

  async downloadAttachment(
    attachmentId: number,
    filename: string,
    onDownloadProgress?: (percent: number | null) => void
  ): Promise<void> {
    const res = await axiosInstance.get(`${BASE}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
      timeout: 0,
      onDownloadProgress: (ev) => {
        if (!onDownloadProgress) return;
        if (ev.total && ev.total > 0) {
          onDownloadProgress(Math.round((ev.loaded * 100) / ev.total));
        } else {
          onDownloadProgress(null);
        }
      },
    });
    triggerBlobDownload(res.data, filename);
  },

  async downloadAllAttachments(
    attachments: Array<Pick<ControlArchiveAttachment, 'id' | 'original_name' | 'size_bytes'>>,
    onDownloadFileProgress?: (info: UploadFileProgressInfo) => void
  ): Promise<void> {
    const list = (attachments || []).filter((a) => a?.id != null);
    if (list.length === 0) return;

    const items = list.map((a) => ({
      name: a.original_name || `파일-${a.id}`,
      sizeBytes: a.size_bytes,
    }));

    for (let i = 0; i < list.length; i++) {
      const att = list[i];
      let lastKnown: number | null = 0;
      const emit = (filePct: number | null) => {
        if (filePct != null) lastKnown = filePct;
        const info = progressFromSequentialItems(
          items,
          i,
          filePct != null ? filePct : lastKnown
        );
        if (filePct == null) info.filePercent = null;
        onDownloadFileProgress?.(info);
      };
      emit(0);
      await controlArchiveService.downloadAttachment(
        att.id,
        att.original_name || `file-${att.id}`,
        (p) => emit(p)
      );
      emit(100);
      if (i < list.length - 1) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }
  },

  async update(
    id: number,
    payload: { title?: string; body?: string }
  ): Promise<ControlArchivePostDetail> {
    const res = await axiosInstance.patch<ControlArchivePostDetail>(`${BASE}/${id}`, payload);
    return res.data;
  },

  async deleteAttachment(attachmentId: number): Promise<ControlArchivePostDetail> {
    const res = await axiosInstance.delete<ControlArchivePostDetail>(
      `${BASE}/attachments/${attachmentId}`
    );
    return res.data;
  },

  /** 기존 글에 첨부 추가 (수정 시). create 와 동일하게 stream 업로드. */
  async addAttachments(
    postId: number,
    files: File[],
    opts?: {
      onUploadProgress?: (percent: number | null) => void;
      onUploadFileProgress?: (info: UploadFileProgressInfo) => void;
    }
  ): Promise<ControlArchivePostDetail> {
    const list = (files || []).slice(0, 5);
    if (list.length === 0) {
      return controlArchiveService.getDetail(postId);
    }
    const items = list.map((f) => ({ name: f.name, sizeBytes: f.size }));
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      await refreshTokenQuiet();
      let lastKnown: number | null = 0;
      const emit = (filePct: number | null) => {
        if (filePct != null) lastKnown = filePct;
        const info = progressFromSequentialItems(
          items,
          i,
          filePct != null ? filePct : lastKnown
        );
        if (filePct == null) info.filePercent = null;
        opts?.onUploadFileProgress?.(info);
        opts?.onUploadProgress?.(info.overallPercent);
      };
      emit(0);
      await axiosInstance.put(`${BASE}/${postId}/attachments/stream`, file, {
        timeout: 0,
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        params: {
          filename: file.name,
          content_type: file.type || undefined,
        },
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        transformRequest: [(data) => data],
        onUploadProgress: (ev) => {
          if (ev.total && ev.total > 0) {
            emit((ev.loaded * 100) / ev.total);
          } else if (file.size > 0 && ev.loaded > 0) {
            emit(Math.min(99, (ev.loaded * 100) / file.size));
          } else {
            emit(null);
          }
        },
      });
      emit(100);
    }
    return controlArchiveService.getDetail(postId);
  },

  async remove(id: number): Promise<void> {
    await axiosInstance.delete(`${BASE}/${id}`);
  },
};
